import { AxiosInstance, AxiosRequestConfig } from "axios";

import { AxiosInstanceInterceptorRequestExtensionI } from "@/api/interceptors/interface";

/**
 * Team → Campaigns (BACKLOG C-14 phase 2 W1, GLE-8392): a campaign's block
 * settings, read and changed by its workspace's LGA or an Admin. Behind the
 * backend switch `user_roles_campaigns_enabled`: while it is off every route
 * answers 404 with error_code 1035 and the page says so. The weekly capacity
 * default is changed through the block pages' capacity route (the shared
 * capacity dialog), never here.
 *
 * Shapes are the contract frozen in the C-14 phase-2 coordination note.
 */

export const CAMPAIGN_BLOCK_SETTING_FIELDS = {
  WEEKLY_BLOCKS_REQUIRED: "weekly_blocks_required",
  DIALING_START_HOUR: "dialing_start_hour",
  DIALING_END_HOUR: "dialing_end_hour",
  BLOCK_STANDARD_DIALS: "block_standard_dials",
  BLOCK_STANDARD_PITCHES: "block_standard_pitches",
  BLOCK_STANDARD_MEETINGS: "block_standard_meetings",
} as const;

export type CampaignBlockSettingFieldI =
  (typeof CAMPAIGN_BLOCK_SETTING_FIELDS)[keyof typeof CAMPAIGN_BLOCK_SETTING_FIELDS];

/**
 * One value per setting. In `settings` a null is "no value of its own" (the
 * platform default applies); in `effective` and `defaults` every value is set.
 * Hours are whole ET hours.
 */
export type CampaignBlockSettingValuesI = Record<
  CampaignBlockSettingFieldI,
  number | null
>;

export interface CampaignBlockSettingsChangeI {
  field: CampaignBlockSettingFieldI;
  old_value: number | null;
  new_value: number | null;
  /** manager (user app), customer (customer app) or admin (staff). */
  actor_type: "manager" | "customer" | "admin";
  actor_user_id: string | null;
  /** Null for staff, or an account that is gone. */
  actor_name: string | null;
  created_at: string;
}

export interface CampaignBlockSettingsI {
  campaign_id: string;
  campaign_name: string | null;
  settings: CampaignBlockSettingValuesI;
  effective: CampaignBlockSettingValuesI;
  last_change: CampaignBlockSettingsChangeI | null;
}

export interface CampaignBlockSettingsListI {
  defaults: CampaignBlockSettingValuesI;
  campaigns: Array<CampaignBlockSettingsI>;
}

/** The fields the PATCH takes: everything but the weekly capacity default. */
export type EditableCampaignBlockSettingFieldI = Exclude<
  CampaignBlockSettingFieldI,
  "weekly_blocks_required"
>;

/**
 * Per setting a value, or `clear_<setting>: true` (back to the default),
 * never both; at least one.
 */
export type UpdateCampaignBlockSettingsRequestI = Partial<
  Record<EditableCampaignBlockSettingFieldI, number> &
    Record<`clear_${EditableCampaignBlockSettingFieldI}`, true>
>;

/**
 * A 404 on the list means "switched off" (or a stale workspace): an answer,
 * not an error to log. A fresh object per request: the meta interceptor
 * writes into `meta`.
 */
const switchProbeConfig = (): AxiosRequestConfig &
  AxiosInstanceInterceptorRequestExtensionI => ({
  meta: { expectedStatuses: [404] },
});

/** The RUI score's rules as the scorer applies them (Admin → Settings). */
export interface DialBlockScoreRulesI {
  starting_score: number;
  floor: number;
  ceiling: number;
  grade_a_min: number;
  grade_b_min: number;
  grade_c_min: number;
  block_base: number;
  metric_bonus_dials: number;
  metric_bonus_pitches: number;
  metric_bonus_meetings: number;
  max_metric_bonus: number;
  max_per_block: number;
  on_time_bonus: number;
  metric_dials: number;
  metric_pitches: number;
  metric_meetings: number;
  streak_tiers: Array<{ min_blocks: number; multiplier: number }>;
  weekly_completion_bonus: number;
  /** Penalties and the weekly decrease are positive amounts taken off. */
  unused_block_penalty: number;
  zero_metric_penalty: number;
  max_weekly_increase: number;
  max_weekly_decrease: number;
  freeze_after_days_final: number;
  freeze_after_days_regardless: number;
}

const workspacePath = (workspaceId: string) =>
  `/v1/manage/workspace/${encodeURIComponent(workspaceId)}`;

const campaignPath = (workspaceId: string, campaignId: string) =>
  `${workspacePath(workspaceId)}/campaign/${encodeURIComponent(campaignId)}/block_settings`;

export const ManageCampaignsAPIEndpoints = (api: AxiosInstance) => ({
  listCampaignBlockSettings: (workspaceId: string) =>
    api.get<CampaignBlockSettingsListI>(
      `${workspacePath(workspaceId)}/campaign_block_settings`,
      switchProbeConfig()
    ),

  updateCampaignBlockSettings: (
    workspaceId: string,
    campaignId: string,
    body: UpdateCampaignBlockSettingsRequestI
  ) =>
    api.patch<{ campaign: CampaignBlockSettingsI }>(
      campaignPath(workspaceId, campaignId),
      body
    ),

  /** Admins only; 404 + 1035 while the W1 switch is off. */
  getScoreRules: () =>
    api.get<{ rules: DialBlockScoreRulesI }>(
      "/v1/manage/dial_block_score_rules",
      switchProbeConfig()
    ),

  /** Newest first; `limit` 1–100 (default 50). */
  listCampaignBlockSettingsHistory: (
    workspaceId: string,
    campaignId: string,
    limit = 50
  ) =>
    api.get<{
      campaign_id: string;
      changes: Array<CampaignBlockSettingsChangeI>;
    }>(`${campaignPath(workspaceId, campaignId)}/history`, {
      params: { limit },
    }),
});
